import React, { useCallback, useMemo, memo, useRef, useEffect } from 'react';
// Import mode components directly from their files
import SetupMode from './SetupMode';
import CustomMode from './CustomMode';
import AutoMode from './AutoMode';
import CompareMode from './CompareMode';
// Update import path, use main Services directory
import { MODES } from '../Services';
// Import the VRP context
import { useVrp } from '../context/VrpContext';
// Import all styles at once
import './styles';

// Using memo to avoid unnecessary re-renders
const Sidebar = memo(function Sidebar() {
  // Use VRP context instead of props
  const { 
    // Mode management
    currentMode, 
    setCurrentMode,
    currentModeRef,
    setupSubmode,
    handleSubmodeSwitching,
    
    // Base state
    locations,
    startId,
    setLocations,
    setStartId,
    maxCapacity,
    setMaxCapacity,
    
    // Path management
    autoPath,
    customPath,
    setPath,
    
    // Map interaction
    setMapPointSelect,
    
    // Vehicle management - Only keep vehicle count for custom paths
    customVehicleCount,
    setCustomVehicleCount
  } = useVrp();
  
  // Add refs and state for resizable sidebar
  const sidebarRef = useRef(null);
  const sidebarContainerRef = useRef(null);
  const resizeHandleRef = useRef(null);
  const isResizingRef = useRef(false);
  
  // Load saved width from localStorage on component mount
  useEffect(() => {
    const savedWidth = localStorage.getItem('vrpSidebarWidth');
    if (savedWidth && sidebarRef.current && sidebarContainerRef.current) {
      sidebarRef.current.style.width = savedWidth;
      sidebarContainerRef.current.style.width = savedWidth;
    }
  }, []);
  
  // Handle sidebar resizing
  useEffect(() => {
    const sidebar = sidebarRef.current;
    const sidebarContainer = sidebarContainerRef.current;
    const resizeHandle = resizeHandleRef.current;
    
    if (!sidebar || !sidebarContainer || !resizeHandle) return;
    
    const handleMouseDown = (e) => {
      e.preventDefault();
      isResizingRef.current = true;
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    };
    
    const handleMouseMove = (e) => {
      if (!isResizingRef.current) return;
      
      // Calculate new width based on mouse position
      const newWidth = e.clientX;
      
      // Apply min and max constraints
      const minWidth = 300;
      const maxWidth = window.innerWidth * 0.5; // 50% of viewport
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        sidebar.style.width = `${newWidth}px`;
        sidebarContainer.style.width = `${newWidth}px`;
        
        // Update the main content area dynamically (match VrpLayout's with-sidebar class)
        const mainContent = document.querySelector('.vrp-main.with-sidebar');
        if (mainContent) {
          mainContent.style.width = `calc(100% - ${newWidth}px)`;
        }
      }
    };
    
    const handleMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        
        // Save width to localStorage
        localStorage.setItem('vrpSidebarWidth', sidebar.style.width);
      }
    };
    
    // Add event listeners
    resizeHandle.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    // Clean up
    return () => {
      resizeHandle.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);
  
  // Use useMemo to optimize component props, reducing unnecessary renders
  const setupModeProps = useMemo(() => ({
    locations,
    startId,
    setupSubmode,
    onSubmodeSwitching: handleSubmodeSwitching,
    setLocations,
    setStartId,
    setPath,
    currentModeRef
  }), [locations, startId, setupSubmode, handleSubmodeSwitching, setLocations, setStartId, setPath, currentModeRef]);
  
  const customModeProps = useMemo(() => ({ 
    locations, 
    startId, 
    setLocations, 
    setStartId, 
    setPath,
    path: customPath,
    setMapPointSelect,
    currentModeRef,
    maxCapacity,
    onMaxCapacityChange: setMaxCapacity,
    vehicleCount: customVehicleCount,
    onVehicleCountChange: setCustomVehicleCount
  }), [locations, startId, setLocations, setStartId, setPath, customPath, setMapPointSelect, currentModeRef, maxCapacity, customVehicleCount, setCustomVehicleCount]);
  
  const autoModeProps = useMemo(() => ({ 
    locations, 
    startId, 
    setLocations, 
    setStartId, 
    setPath,
    path: autoPath,
    currentModeRef,
    maxCapacity,
    onMaxCapacityChange: setMaxCapacity
    // No longer pass vehicleCount and onVehicleCountChange as auto paths are determined by algorithm
  }), [locations, startId, setLocations, setStartId, setPath, autoPath, currentModeRef, maxCapacity]);
  
  const compareModeProps = useMemo(() => ({
    locations,
    startId,
    autoPath,
    customPath,
    currentModeRef,
    maxCapacity,
    vehicleCount: customVehicleCount
  }), [locations, startId, autoPath, customPath, currentModeRef, maxCapacity, customVehicleCount]);
  
  // Add log to track passed vehicle count
  useEffect(() => {

  }, [customVehicleCount, autoPath, customPath]);
  
  // Placeholder render function for components that fail to load
  const renderPlaceholder = (name) => (
    <div className="error-placeholder">
      <h3>Error: Unable to load {name} component</h3>
      <p>Please check if the component file exists and is exported correctly.</p>
    </div>
  );
  
  // Safely render a component
  const safeRender = (Component, props = {}) => {
    return Component ? <Component {...props} /> : renderPlaceholder('Mode');
  };
  
  // Handle mode tab click - optimized with useCallback
  const handleModeClick = useCallback((mode) => {
    setCurrentMode(mode);
  }, [setCurrentMode]);

  return (
    <div className="vrp-sidebar-container" ref={sidebarContainerRef}>
      <div className="vrp-sidebar" ref={sidebarRef}>
        <div className="mode-selector">
          {MODES.map(mode => (
            <button
              key={mode}
              className={`mode-tab${currentMode === mode ? ' active' : ''}`}
              onClick={() => handleModeClick(mode)}
            >
              {mode}
            </button>
          ))}
        </div>
        <div className="mode-content">
          {currentMode === MODES[0] && safeRender(SetupMode, setupModeProps)}
          {currentMode === MODES[1] && safeRender(CustomMode, customModeProps)}
          {currentMode === MODES[2] && safeRender(AutoMode, autoModeProps)}
          {currentMode === MODES[3] && safeRender(CompareMode, compareModeProps)}
        </div>
      </div>
      <div className="vrp-resize-handle" ref={resizeHandleRef}></div>
    </div>
  );
});

export default Sidebar; 